import { isGameLive, type GameContext } from '@/lib/nflGameLine';

/**
 * SAB-1009: the Sleeper score/projection interplay for matchup rows.
 *
 * Presentation only — every number here already exists (SAB-994's weekly projections and the
 * scoring engine's actuals). What was missing is WHICH of the two is the headline at any
 * moment, and the matchup showed a flat +0.0 for everyone before kickoff, which reads as
 * "your whole team scored nothing" rather than "nobody has played yet".
 */

/** Finished states, matching the exclusion heuristic used across the NFL surfaces. */
const FINISHED = new Set(['closed', 'complete', 'completed', 'final']);

/**
 * SAB-1009 revision: is this player's game FINAL?
 *
 * ── NOT THE SCORE-SLOT THRESHOLD. USE hasPlayed() FOR THAT. ─────────────────
 *
 * This briefly WAS the score-slot threshold, and it was wrong for a live Sunday: NFL stats
 * flow every 3 minutes (crons 40/41), so gating on 'final' would have hidden a player's
 * accruing points behind a dash until his game closed. The rows now flip at KICKOFF via
 * hasPlayed(), and so does projectedFinal, so the header and the rows agree all afternoon.
 *
 * Kept, with no current caller, because the distinction it draws is real and worth having
 * named: 'the game has ended' is a different question from 'the game has begun', and a
 * future surface (a settled-results view, a recap) will want the strict one. Its tests
 * pin that difference. Do not wire it into the score slot again without re-reading the
 * paragraph above.
 */
export function isGameFinal(game: GameContext | null | undefined): boolean {
  if (!game) return false;
  return FINISHED.has(String(game.status || '').toLowerCase());
}

/**
 * Has this player's game STARTED?
 *
 * Two independent signals, ORed: the provider status has left 'scheduled', or the kickoff
 * timestamp has passed. Either alone is insufficient — a provider that is slow to flip
 * status would keep a live player showing a projection long after kickoff, and a missing
 * kickoff timestamp would do the reverse.
 *
 * No game at all (bye, or a franchise with no row this week) counts as NOT played: a bye
 * player never will, and showing his projection as though it were pending would be a lie.
 * Callers handle the bye case explicitly via `isBye`.
 */
export function hasPlayed(game: GameContext | null | undefined, now: Date = new Date()): boolean {
  if (!game || !game.scheduled) return false;
  if (isGameLive(game.status)) return true;
  const s = String(game.status || '').toLowerCase();
  if (FINISHED.has(s)) return true;
  const kickoff = new Date(game.scheduled);
  return !Number.isNaN(kickoff.getTime()) && kickoff <= now;
}

export interface ScoreCell {
  /** What the score slot shows on the NAME line. */
  primary: string;
  /** Kept so callers can style a dash differently from a real number. */
  isPlaceholder: boolean;
}

const fmt = (v: number) => (v >= 0 ? `+${v.toFixed(1)}` : v.toFixed(1));

/**
 * SAB-1009 revision: the score slot on the name line.
 *
 * A dash until the game is final, then the actual. The projection is NOT here any more --
 * it moved to the second line beside the game info, where it is visible at all times.
 *
 * ── WHY THE PROJECTION LEFT THIS SLOT ───────────────────────────────────────
 *
 * Putting it here meant one position held two different kinds of number depending on the
 * clock, so a reader had to know the game state to know what they were looking at. Split
 * across two lines, the top line is always "what he scored" and the second is always "what
 * we expect" -- neither ever changes meaning. That is the Sleeper shape.
 *
 * A bye still shows a dash and, because it can never become an actual, that dash is
 * permanent rather than pending. Both render identically; the row's BYE chip is what tells
 * them apart, and it already does.
 */
export function scoreCell(
  actual: number | null,
  isFinal: boolean,
  isBye = false,
): ScoreCell {
  if (isBye || !isFinal || actual === null || actual === undefined) {
    return { primary: '–', isPlaceholder: true };
  }
  return { primary: fmt(actual), isPlaceholder: false };
}

/** The projection as it appears on the second line. Null renders as nothing, never 0.0. */
export function projectionLabel(projected: number | null | undefined): string | null {
  return projected === null || projected === undefined ? null : projected.toFixed(1);
}

/** Canonical display order, so two sides of a matchup never disagree about ordering. */
const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE', 'FB', 'K', 'P', 'DST', 'COACH'];

export interface YetToPlay {
  count: number;
  /** e.g. "QB, 3 RB, 3 WR, K, DST". Empty string when nobody is left. */
  label: string;
}

/**
 * "Yet to play (N)" with the position breakdown.
 *
 * Counts STARTERS only — a bench player cannot contribute to the matchup, so including him
 * would overstate what is still to come, which is the one thing this summary exists to
 * answer.
 *
 * Players on a bye are excluded: they are not "yet to play", they are not playing. Counting
 * them would mean the number never reaches zero for a team with a bye starter, and a
 * summary that cannot reach zero is not a summary.
 *
 * A count of 1 is rendered bare ("QB"), more than 1 with the count ("3 WR") — the Sleeper
 * shape, and it reads as a list rather than as arithmetic.
 */
export function yetToPlaySummary(
  players: { position: string; game?: GameContext | null; isBye?: boolean }[],
  now: Date = new Date(),
): YetToPlay {
  const pending = players.filter(p => !p.isBye && !hasPlayed(p.game, now));
  const counts = new Map<string, number>();
  for (const p of pending) counts.set(p.position, (counts.get(p.position) ?? 0) + 1);

  const ordered = [...counts.entries()].sort((a, b) => {
    const ia = POSITION_ORDER.indexOf(a[0]);
    const ib = POSITION_ORDER.indexOf(b[0]);
    // Unknown positions sort last rather than to the front, which is what indexOf's -1
    // would otherwise do.
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a[0].localeCompare(b[0]);
  });

  return {
    count: pending.length,
    label: ordered.map(([pos, n]) => (n > 1 ? `${n} ${pos}` : pos)).join(', '),
  };
}

/**
 * A side's projected FINAL total: what is already banked, plus what is still expected.
 *
 * Deliberately not "sum of projections". Once half a lineup has played, the projection for
 * those players is history and the actual is the fact; adding projections on top of actuals
 * would double-count, and using projections alone would ignore the result so far.
 *
 * `projectedRemaining` is null when nothing is left to play, which lets the UI stop showing
 * a projection line on a finished matchup instead of repeating the final score twice.
 */
export function projectedFinal(
  players: { position: string; game?: GameContext | null; isBye?: boolean }[],
  actualOf: (p: unknown) => number,
  projectedOf: (p: unknown) => number | null | undefined,
  now: Date = new Date(),
): { banked: number; projectedRemaining: number | null; projectedFinal: number } {
  let banked = 0;
  let remaining = 0;
  let anyRemaining = false;
  for (const p of players) {
    // SAB-1009: banked once the game has STARTED, matching what the rows show. Live stats
    // flow every 3 minutes (crons 40/41), so a started player's actual is real and rising --
    // projected final is therefore actual-so-far plus a projection for everyone unstarted,
    // which is the standard presentation and keeps the header and the rows telling the same
    // story at every moment of a Sunday.
    if (hasPlayed(p.game, now)) { banked += actualOf(p); continue; }
    if (p.isBye) continue;
    const proj = projectedOf(p);
    if (proj !== null && proj !== undefined) { remaining += proj; anyRemaining = true; }
  }
  return {
    banked: Math.round(banked * 100) / 100,
    projectedRemaining: anyRemaining ? Math.round(remaining * 100) / 100 : null,
    projectedFinal: Math.round((banked + remaining) * 100) / 100,
  };
}
